/* The one warehouse control — lives in the topbar.
 *
 * A head picks a store here and the whole app is in that store until they change
 * it. It is deliberately prominent and always visible: every figure on every
 * screen is now scoped to this value, so it must never be a mystery which store
 * you are looking at.
 *
 * A PH user has exactly one store, so they get a label rather than a dead
 * dropdown — the information still matters (it names what they are working in),
 * the choice does not exist.
 */
import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Search, Warehouse } from "lucide-react";
import { useWarehouse } from "./WarehouseContext";
import { ALL_STORES } from "../../Services/warehouseContext";

const WarehouseSwitcher = () => {
  const { code, isAll, name, warehouses, isHead, select } = useWarehouse();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  if (!isHead) {
    return (
      <span
        className="hidden md:flex items-center gap-2 h-8 px-2.5 rounded-cb-sm bg-sunken border border-hairline-subtle max-w-[260px]"
        title={name || code}
      >
        <Warehouse size={14} strokeWidth={1.7} className="text-fg-muted shrink-0" />
        <span className="text-t-sm font-semibold text-fg-default truncate">{name || code || "No store"}</span>
      </span>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? warehouses.filter((w) => `${w.id} ${w.name || ""}`.toLowerCase().includes(q))
    : warehouses;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={
          isAll
            ? "Reporting across every store · click to pick one"
            : code
            ? `Working in ${name || code} · click to switch store`
            : "Select the store to work in"
        }
        className={[
          "flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-cb-sm border transition-colors max-w-[180px] sm:max-w-[300px]",
          code || isAll
            ? "bg-sunken border-hairline-subtle hover:bg-surface-hover"
            : /* Nothing chosen is not a neutral state — every screen is empty until
                 it is, so the control says so instead of looking settled. */
              "bg-amber-50 border-amber-200 hover:bg-amber-100",
        ].join(" ")}
      >
        <Warehouse
          size={14}
          strokeWidth={1.7}
          className={code || isAll ? "text-fg-muted shrink-0" : "text-amber-700 shrink-0"}
        />
        <span className="text-left leading-tight min-w-0 hidden sm:block">
          <span className="block text-[9px] uppercase tracking-[0.08em] text-fg-muted font-semibold">
            Store
          </span>
          <span
            className={[
              "block text-t-sm font-semibold truncate",
              code || isAll ? "text-fg-strong" : "text-amber-700",
            ].join(" ")}
          >
            {isAll ? "All stores" : code ? name || code : "Select a store"}
          </span>
        </span>
        <ChevronDown size={13} strokeWidth={1.9} className="text-fg-muted shrink-0" />
      </button>

      {open && (
        <div className="absolute top-[40px] right-0 w-[320px] max-w-[calc(100vw-16px)] bg-panel border border-hairline-subtle rounded-cb-md shadow-cb-md z-50 cb-animate-in overflow-hidden">
          <div className="p-2 border-b border-hairline-subtle">
            <div className="relative flex items-center">
              <Search size={13} strokeWidth={1.7} className="absolute left-2.5 text-fg-muted" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search store name or code…"
                className="w-full h-8 pl-8 pr-2 text-t-sm bg-panel border border-hairline rounded-cb-sm text-fg-default outline-none"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto cb-scroll p-1">
            {/* Reporting across the estate. Offered first because it is the one
                entry that is not a place. Stock levels and anything that writes
                (billing, inward, put-away) still need a single store and say so. */}
            {!q && (
              <button
                onClick={() => {
                  select(ALL_STORES);
                  setOpen(false);
                }}
                className={[
                  "w-full flex items-center gap-2 px-2.5 py-1.5 mb-1 rounded-cb-sm text-left transition-colors border-b border-hairline-subtle",
                  isAll ? "bg-surface-selected" : "hover:bg-surface-hover",
                ].join(" ")}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-t-sm font-semibold text-fg-strong">All stores</span>
                  <span className="block text-[10px] text-fg-muted">
                    Reports across every store · stock and billing need one
                  </span>
                </span>
                {isAll && <Check size={14} strokeWidth={2} className="text-ink-600 shrink-0" />}
              </button>
            )}
            {filtered.length === 0 && (
              <div className="px-2.5 py-2 text-t-sm text-fg-muted">No stores match.</div>
            )}
            {filtered.map((w) => {
              const active = !isAll && String(w.id) === String(code);
              return (
                <button
                  key={w.id}
                  onClick={() => {
                    select(w.id);
                    setOpen(false);
                  }}
                  className={[
                    "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-cb-sm text-left transition-colors",
                    active ? "bg-surface-selected" : "hover:bg-surface-hover",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-t-sm font-semibold text-fg-strong truncate">
                      {w.name || w.id}
                    </span>
                    <span className="block text-[10px] font-mono text-fg-muted">{w.id}</span>
                  </span>
                  {active && <Check size={14} strokeWidth={2} className="text-ink-600 shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="px-2.5 py-2 border-t border-hairline-subtle text-[10px] text-fg-muted">
            Every screen is scoped to this selection.
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseSwitcher;
